import type { GptSize } from './gpt';

const GAM_PUBLISHER_ID = '7449';

export const GPT_SCRIPT_ORIGIN = 'https://securepubads.g.doubleclick.net';
export const GPT_SCRIPT_SRC = `${GPT_SCRIPT_ORIGIN}/tag/js/gpt.js`;

// 1x1 beacon + banner + fluid
export const SRP_AD_SIZES: ReadonlyArray<GptSize> = [[1, 1], [805, 115], 'fluid'];

export type SrpAdSlot = {
  /** GPT container id. Device-independent on purpose: the phone vs desktop ad
   *  unit is only known after mount, and an id that changed with it would
   *  remount the div out from under the ad GPT had already rendered into it. */
  id: string;
  format: string;
  /** 0-based position in the listings array the slot is spliced into. */
  index: number;
};

export const SRP_AD_SLOTS: ReadonlyArray<SrpAdSlot> = [
  { id: 'div-gpt-ad-srp-search_1', format: 'search_1', index: 2 },
  { id: 'div-gpt-ad-srp-search_2', format: 'search_2', index: 17 },
];

/** Below this many results legacy renders no SRP ads at all (`AreaListingsContainer`, `MobileSRP` List). */
const MIN_LISTINGS_FOR_ADS = 2;

export type SrpAdSlotPlacement = {
  slot: SrpAdSlot;
  /** Where to splice the slot into the rendered list, in order. */
  index: number;
};

/**
 * Both slots and where they land in a result set of `listingCount`.
 *
 * A slot whose index is past the end is pinned to the end rather than dropped:
 * legacy splices sequentially into the growing array, so `splice(17, …)` on a
 * 10-result page appends the ad after the last card and GAM is still asked for
 * it. Dropping it instead — which this app used to do — silently halves SRP
 * inventory on every search returning 17 or fewer results.
 */
export function getSrpAdSlotPlacements(listingCount: number): ReadonlyArray<SrpAdSlotPlacement> {
  if (listingCount <= MIN_LISTINGS_FOR_ADS) return [];

  let renderedLength = listingCount;
  return SRP_AD_SLOTS.map((slot) => {
    const index = Math.min(slot.index, renderedLength);
    renderedLength += 1;
    return { slot, index };
  });
}

export type SrpAdDevice = 'phone' | 'tablet' | 'desktop';

const AD_UNIT_BY_DEVICE: Record<SrpAdDevice, string> = {
  phone: 'hotpads_phone_mweb',
  tablet: 'hotpads_tablet_mweb',
  desktop: 'HotPads',
};

// Legacy hotpads-web's ad breakpoints (browserUtils legend), not this app's
// Panda scale: GAM attributes requests and revenue to the phone/tablet/desktop
// ad units by these widths, so reporting only stays comparable across the two
// apps if the buckets match exactly.
const MAX_PHONE_WIDTH = 767;
const MAX_TABLET_WIDTH = 991;

export function getSrpAdDevice(viewportWidth: number): SrpAdDevice {
  if (viewportWidth <= MAX_PHONE_WIDTH) return 'phone';
  if (viewportWidth <= MAX_TABLET_WIDTH) return 'tablet';
  return 'desktop';
}

export function getSrpAdSlotPath(device: SrpAdDevice, format: string, adListingType: string): string {
  return `/${GAM_PUBLISHER_ID}/${AD_UNIT_BY_DEVICE[device]}/Search_Results/${adListingType}/${format}`;
}
